/**
 * 70.1: which parts of a tree model touch which, and which stand on the ground through the parts they touch — so a
 * damaged or felled form never leaves a part in the air (rendering/treeForms.ts) and a test can say so. A part is one
 * mesh of the kit's shapes (blob, cone, cylinder, box, lathe), read from its own geometry's parameters in its own space;
 * two parts touch when a point of one's surface (its vertices and its faces' centres) lies inside the other, give or take
 * `tol`. Presentation only (three's math; no state).
 */
import * as THREE from 'three';

/** How near counts as touching, in the model's units (a kit tree is ~0.25–0.4 tall). */
export const CONTACT_TOL = 0.0015;

const _p = new THREE.Vector3(), _v = new THREE.Vector3();

/** Is `p` (the mesh's own space) inside the mesh's shape, within `tol` (its own space)? Null for a shape it can't read. */
function insideLocal(m: THREE.Mesh, p: THREE.Vector3, tol: number): boolean | null {
  const geo = m.geometry, type = geo.type;
  const rad = Math.hypot(p.x, p.z);
  if (type === 'IcosahedronGeometry' || type === 'PolyhedronGeometry') {
    // a lumpy blob: its own vertices' mean distance from its centre
    const r = blobRadius(geo);
    return p.length() <= r + tol;
  }
  if (type === 'ConeGeometry' || type === 'CylinderGeometry') {
    const q = (geo as THREE.CylinderGeometry).parameters;
    const h = q.height, top = type === 'ConeGeometry' ? 0 : q.radiusTop, bot = type === 'ConeGeometry' ? (geo as THREE.ConeGeometry).parameters.radius : q.radiusBottom;
    if (p.y < -h / 2 - tol || p.y > h / 2 + tol) return false;
    const t = Math.min(1, Math.max(0, (p.y + h / 2) / h));
    return rad <= bot + (top - bot) * t + tol;
  }
  if (type === 'BoxGeometry') {
    const q = (geo as THREE.BoxGeometry).parameters;
    return Math.abs(p.x) <= q.width / 2 + tol && Math.abs(p.y) <= q.height / 2 + tol && Math.abs(p.z) <= q.depth / 2 + tol;
  }
  if (type === 'LatheGeometry') {
    const pts = (geo as THREE.LatheGeometry).parameters.points;
    if (p.y < pts[0].y - tol || p.y > pts[pts.length - 1].y + tol) return false;
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1], b = pts[i];
      if (p.y <= b.y + tol || i === pts.length - 1) {
        const t = b.y > a.y ? Math.min(1, Math.max(0, (p.y - a.y) / (b.y - a.y))) : 0;
        return rad <= a.x + (b.x - a.x) * t + tol;
      }
    }
    return false;
  }
  return null;
}
const blobRadii = new WeakMap<THREE.BufferGeometry, number>();
function blobRadius(geo: THREE.BufferGeometry): number {
  let r = blobRadii.get(geo);
  if (r === undefined) {
    const pos = geo.attributes.position;
    let s = 0;
    for (let i = 0; i < pos.count; i++) s += _v.fromBufferAttribute(pos, i).length();   // (not _p: it holds the point being tested)
    r = s / Math.max(1, pos.count);
    blobRadii.set(geo, r);
  }
  return r;
}

/** A part of a model: its mesh, its surface points in the model's space, and the model-to-mesh map. */
export interface ContactPart { mesh: THREE.Mesh; points: THREE.Vector3[]; toLocal: THREE.Matrix4; scale: number }

/** The model's parts, in the model's own space (the root's frame), as they stand now. */
export function contactParts(root: THREE.Object3D): ContactPart[] {
  root.updateMatrixWorld(true);
  const rootInv = new THREE.Matrix4().copy(root.matrixWorld).invert();
  const parts: ContactPart[] = [];
  root.traverse(o => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    const toModel = new THREE.Matrix4().multiplyMatrices(rootInv, m.matrixWorld);
    const pos = m.geometry.attributes.position, idx = m.geometry.index;
    const points: THREE.Vector3[] = [];
    for (let i = 0; i < pos.count; i++) points.push(new THREE.Vector3().fromBufferAttribute(pos, i).applyMatrix4(toModel));
    // the faces' centres too (a cylinder's sides have no vertex mid-way)
    const n = idx ? idx.count : pos.count;
    for (let i = 0; i + 2 < n; i += 3) {
      const a = idx ? idx.getX(i) : i, b = idx ? idx.getX(i + 1) : i + 1, c = idx ? idx.getX(i + 2) : i + 2;
      points.push(new THREE.Vector3().fromBufferAttribute(pos, a).add(_p.fromBufferAttribute(pos, b)).add(_p.fromBufferAttribute(pos, c)).divideScalar(3).applyMatrix4(toModel));
    }
    const s = new THREE.Vector3().setFromMatrixScale(toModel);
    parts.push({ mesh: m, points, toLocal: new THREE.Matrix4().copy(toModel).invert(), scale: Math.max(1e-9, Math.min(Math.abs(s.x), Math.abs(s.y), Math.abs(s.z))) });
  });
  return parts;
}

/** Is model-space point `p` inside part `b`, within `tol` (model units)? */
export function insidePart(b: ContactPart, p: THREE.Vector3, tol = CONTACT_TOL): boolean {
  return insideLocal(b.mesh, _p.copy(p).applyMatrix4(b.toLocal), tol / b.scale) === true;
}

/** A point where `a` touches `b` — the one of a's surface points inside b nearest a's centre — or null when they don't. */
export function contactPoint(a: ContactPart, b: ContactPart, tol = CONTACT_TOL): THREE.Vector3 | null {
  const c = new THREE.Vector3();
  for (const p of a.points) c.add(p);
  c.divideScalar(Math.max(1, a.points.length));
  let best: THREE.Vector3 | null = null, bd = Infinity;
  for (const p of a.points) if (insidePart(b, p, tol)) { const d = p.distanceToSquared(c); if (d < bd) { bd = d; best = p; } }
  if (best) return best.clone();
  // or one of b's points inside a (a small part sunk in a big one)
  for (const p of b.points) if (insidePart(a, p, tol)) return p.clone();
  return null;
}

/** Do parts `a` and `b` touch? */
export function touches(a: ContactPart, b: ContactPart, tol = CONTACT_TOL): boolean {
  return a.points.some(p => insidePart(b, p, tol)) || b.points.some(p => insidePart(a, p, tol));
}

/**
 * The parts standing on the ground (y ≤ `ground` + tol) through the parts they touch: for each part the index of the part
 * it was reached from (−1: on the ground itself), or undefined when it is in the air. Breadth first, in the parts' order,
 * so a part hangs from the first part nearer the ground that holds it.
 */
export function supportOf(parts: readonly ContactPart[], ground = 0, tol = CONTACT_TOL): Array<number | undefined> {
  const from: Array<number | undefined> = parts.map(() => undefined);
  const queue: number[] = [];
  parts.forEach((p, i) => { if (p.points.some(q => q.y <= ground + tol)) { from[i] = -1; queue.push(i); } });
  for (let h = 0; h < queue.length; h++) {
    const i = queue[h];
    parts.forEach((p, j) => { if (from[j] === undefined && touches(parts[i], p, tol)) { from[j] = i; queue.push(j); } });
  }
  return from;
}

/** The meshes of a model held up by nothing (no chain of touching parts down to the ground). */
export function floatingParts(root: THREE.Object3D, ground = 0, tol = CONTACT_TOL): THREE.Mesh[] {
  const parts = contactParts(root), from = supportOf(parts, ground, tol);
  return parts.filter((_, i) => from[i] === undefined).map(p => p.mesh);
}
